import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";

export type SubscriptionTier = "free" | "pro";
export type SubscriptionStatus = "active" | "cancelled" | "expired" | "trialing";

export interface Subscription {
  tier: SubscriptionTier;
  status: SubscriptionStatus;
  billingCycle: "monthly" | "annual" | null;
  currentPeriodEnd: Date | null;
  cancelAtPeriodEnd: boolean;
}

interface SubscriptionCtx {
  subscription: Subscription | null;
  isPro: boolean;
  isFree: boolean;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const DEFAULT_FREE: Subscription = {
  tier: "free",
  status: "active",
  billingCycle: null,
  currentPeriodEnd: null,
  cancelAtPeriodEnd: false,
};

const Ctx = createContext<SubscriptionCtx>({
  subscription: DEFAULT_FREE,
  isPro: false,
  isFree: true,
  isLoading: true,
  error: null,
  refetch: async () => {},
});

// Single source of truth for subscription state + the realtime channel.
// Mounted ONCE at the app root (see App.tsx) — every ProGate / useSubscription()
// call just reads from this context instead of opening its own Supabase
// realtime channel. Multiple components opening a channel with the same
// name (`subscription:${user.id}`) is what caused the
// "cannot add 'postgres_changes' callbacks ... after subscribe()" crash.
export const SubscriptionProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSubscription = useCallback(async () => {
    if (!user) {
      setSubscription(DEFAULT_FREE);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const { data, error: dbError } = await supabase
        .from("subscriptions")
        .select("tier, status, billing_cycle, current_period_end, cancel_at_period_end")
        .eq("user_id", user.id)
        .single();

      if (dbError) throw dbError;

      if (!data) {
        setSubscription(DEFAULT_FREE);
        return;
      }

      const effectiveTier: SubscriptionTier =
        data.status === "expired" || data.status === "cancelled" ? "free" : data.tier;

      setSubscription({
        tier: effectiveTier,
        status: data.status,
        billingCycle: data.billing_cycle ?? null,
        currentPeriodEnd: data.current_period_end ? new Date(data.current_period_end) : null,
        cancelAtPeriodEnd: data.cancel_at_period_end ?? false,
      });
    } catch (err) {
      console.error("useSubscription error:", err);
      setError("Failed to load subscription");
      setSubscription(DEFAULT_FREE); // fail open to free
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchSubscription();

    if (!user) return;

    const channel = supabase
      .channel(`subscription:${user.id}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "subscriptions", filter: `user_id=eq.${user.id}` },
        () => fetchSubscription()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, fetchSubscription]);

  const isPro =
    subscription?.tier === "pro" && (subscription.status === "active" || subscription.status === "trialing");

  return (
    <Ctx.Provider
      value={{
        subscription,
        isPro,
        isFree: !isPro,
        isLoading,
        error,
        refetch: fetchSubscription,
      }}
    >
      {children}
    </Ctx.Provider>
  );
};

export const useSubscriptionContext = () => useContext(Ctx);
